import React, { useEffect, useState } from "react"
import {
  IconChevronDown,
  IconChevronRight,
  IconHome,
  IconArrowRight,
  IconArrowLeft,
  IconBook2,
} from "@tabler/icons-react"
import analytics, {
  ANALYTICS_EVENT_NAMES,
  useModalAnalytics,
} from "@love-irish/analytics"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@love-irish/ui-components/dialog"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@love-irish/ui-components/dropdown-menu"

import AdventureStoryPreview from "@love-irish/adventure-story-preview"
import { GrammarSections } from "./grammar_reference_modal"
import { SectionCard, SectionCards } from "./section_cards"
import {
  PAGES,
  PageKey,
  LANGUAGE_SECTIONS,
  findSection,
} from "./language_sections"
import { twMerge } from "tailwind-merge"

/**
 * The app-wide Irish language reference: a home screen that jumps to Spelling, Pronunciation, Grammar,
 * Word order (VSO), and Verb conjugation, each a paged guide. The body is Dialog-free so the same
 * content can later mount standalone (a static reference page); the modal just wraps it.
 */

/** The reusable body (no Dialog): the home "contents" grid, or the selected chapter's guide. When a
 *  chapter is paged, `chapterPage`/`onChapterPageChange` let the parent (the modal) own paging so the
 *  breadcrumb can jump back to the chapter root. */
export const LanguageReferenceBody: React.FC<{
  page: PageKey
  onNavigate: (page: PageKey) => void
  chapterPage?: number
  onChapterPageChange?: (page: number) => void
  modalSessionId?: string | null
}> = ({
  page,
  onNavigate,
  chapterPage,
  onChapterPageChange,
  modalSessionId,
}) => {
  const section = findSection(page)

  if (page === PAGES.HOME || !section) {
    return (
      <SectionCards
        testId="language-home"
        renderCard={(item, number) => (
          <SectionCard
            number={number}
            icon={item.icon}
            label={item.label}
            description={item.description}
            onClick={() => onNavigate(item.key)}
          />
        )}
      />
    )
  }

  return (
    <GrammarSections
      entry={section.entry}
      renderHeader={false}
      analyticsName={`irish_language:${section.key}`}
      page={chapterPage}
      onPageChange={onChapterPageChange}
      modalSessionId={modalSessionId}
    />
  )
}

/** The breadcrumb "jump to chapter" dropdown: a bare caret at the end of the header text. */
const SectionMenu: React.FC<{ onNavigate: (page: PageKey) => void }> = ({
  onNavigate,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger
      aria-label="Jump to a chapter"
      className="inline-flex items-center rounded p-0.5 text-muted-foreground hover:bg-secondary-100 hover:text-foreground"
    >
      <IconChevronDown size={16} aria-hidden />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      <DropdownMenuItem onClick={() => onNavigate(PAGES.HOME)}>
        Contents
      </DropdownMenuItem>
      {LANGUAGE_SECTIONS.map((item, index) => (
        <DropdownMenuItem key={item.key} onClick={() => onNavigate(item.key)}>
          {index + 1}. {item.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
)

/**
 * The Irish language reference modal. Opens to `defaultPage` (default HOME). The header is a
 * breadcrumb (home icon > section, with a caret to jump elsewhere) and a footer steps to the next
 * section.
 *
 * ```tsx
 * import { IrishLanguageModal, PAGES } from "@love-irish/grammar-reference"
 * <IrishLanguageModal open={open} onOpenChange={setOpen} defaultPage={PAGES.PRONUNCIATION} />
 * ```
 */
export const IrishLanguageModal: React.FC<{
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultPage?: PageKey
  /** Open a paged chapter (e.g. Grammar) at a specific page, so a dictionary grammar term can deep-link
   *  straight to its point and the reader can keep paging. */
  defaultChapterPage?: number
}> = ({
  open,
  onOpenChange,
  defaultPage = PAGES.HOME,
  defaultChapterPage = 0,
}) => {
  const [page, setPage] = useState<PageKey>(defaultPage)
  // The page within a paged chapter (owned here so the breadcrumb can jump back to the chapter root).
  const [chapterPage, setChapterPage] = useState(defaultChapterPage)
  const { modalSessionId } = useModalAnalytics("irish_language", open)

  // Open to the requested page (and chapter page) each time – reuse one modal for every entry point.
  useEffect(() => {
    if (open) {
      setPage(defaultPage)
      setChapterPage(defaultChapterPage)
    }
  }, [open, defaultPage, defaultChapterPage])

  // Move to a chapter, always starting at its root page. (Navigation resets here rather than in an
  // effect, so a deep-link opening past the root is not clobbered.)
  const goToChapter = (key: PageKey) => {
    setPage(key)
    setChapterPage(0)
  }

  // Section-level page view (home ↔ a section), stitched to this open via modalSessionId.
  useEffect(() => {
    if (!open) return
    analytics.track(ANALYTICS_EVENT_NAMES.MODAL_PAGE_VIEWED, {
      name: "irish_language",
      page,
      modalSessionId,
    })
  }, [page, open, modalSessionId])

  const section = findSection(page)
  // The neighboring sections in order (for the footer stepper). Undefined on home, and at the ends.
  const currentIndex = LANGUAGE_SECTIONS.findIndex((item) => item.key === page)
  const prevSection =
    section && currentIndex > 0
      ? LANGUAGE_SECTIONS[currentIndex - 1]
      : undefined
  const nextSection = section ? LANGUAGE_SECTIONS[currentIndex + 1] : undefined
  // A paged chapter (not single-page, more than one page) that is currently past its root: the
  // breadcrumb label becomes a link back to page 1.
  const isPagedChapter = Boolean(
    section && !section.entry.singlePage && section.entry.slides.length > 1
  )
  const canReturnToRoot = isPagedChapter && chapterPage > 0
  const isHome = page === PAGES.HOME

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 border-0 bg-transparent p-0 shadow-none sm:max-w-2xl">
        {/* The whole dialog is the book's page (light, bg-background). The cover sits at the top and
            spreads wide once you open a chapter; only the content inside the page scrolls. */}
        <AdventureStoryPreview
          cover
          spread={!isHome}
          scrollBody
          className={twMerge(
            " border border-secondary-200 bg-background p-5 font-sans text-base text-foreground shadow-lg",
            isHome && "rounded-lg rounded-tl-none"
          )}
          title={
            <div className="flex flex-col gap-3">
              <DialogHeader className="space-y-0">
                {/* Breadcrumb: home icon (back to the picker) > current section, with a caret to jump. */}
                <DialogTitle className="flex items-center gap-1.5 text-base">
                  <button
                    type="button"
                    onClick={() => goToChapter(PAGES.HOME)}
                    aria-label="Reference home"
                    className="rounded p-0.5 text-muted-foreground hover:bg-secondary-100 hover:text-foreground"
                  >
                    <IconHome size={16} aria-hidden />
                  </button>
                  {section ? (
                    <>
                      <IconChevronRight
                        size={14}
                        className="text-muted-foreground"
                        aria-hidden
                      />
                      {canReturnToRoot ? (
                        // On a deeper page of a paged chapter, the label links back to the chapter root.
                        <button
                          type="button"
                          onClick={() => setChapterPage(0)}
                          className="hover:underline"
                        >
                          {section.label}
                        </button>
                      ) : (
                        <span>{section.label}</span>
                      )}
                    </>
                  ) : (
                    <span>Irish language</span>
                  )}
                  <SectionMenu onNavigate={goToChapter} />
                </DialogTitle>
              </DialogHeader>
              {/* Always present so the dialog has an accessible description (Radix requires one). */}
              <DialogDescription>
                {section
                  ? section.description
                  : "A quick reference for spelling, pronunciation, grammar, and word order."}
              </DialogDescription>

              <LanguageReferenceBody
                page={page}
                onNavigate={goToChapter}
                chapterPage={chapterPage}
                onChapterPageChange={setChapterPage}
                modalSessionId={modalSessionId}
              />

              {/* Step to the previous / next section (each shown with its name). Hidden on home; each side
            hides at its end of the list. */}
              {section && (prevSection || nextSection) && (
                <div className="mt-1 flex items-center justify-between border-t border-secondary-100 pt-3">
                  {prevSection ? (
                    <button
                      type="button"
                      onClick={() => goToChapter(prevSection.key)}
                      className="group flex items-center gap-2 text-left"
                    >
                      <IconArrowLeft
                        size={16}
                        className="text-muted-foreground"
                        aria-hidden
                      />
                      <span className="flex flex-col">
                        <span className="text-xs text-muted-foreground">
                          Previous chapter
                        </span>
                        <span className="text-sm font-medium text-primary-600 group-hover:underline">
                          {prevSection.label}
                        </span>
                      </span>
                    </button>
                  ) : (
                    // Spacer so a lone Next stays right-aligned.
                    <span />
                  )}
                  {nextSection && (
                    <button
                      type="button"
                      onClick={() => goToChapter(nextSection.key)}
                      className="group flex items-center gap-2 text-right"
                    >
                      <span className="flex flex-col">
                        <span className="text-xs text-muted-foreground">
                          Next chapter
                        </span>
                        <span className="text-sm font-medium text-primary-600 group-hover:underline">
                          {nextSection.label}
                        </span>
                      </span>
                      <IconArrowRight
                        size={16}
                        className="text-muted-foreground"
                        aria-hidden
                      />
                    </button>
                  )}
                </div>
              )}
            </div>
          }
        />
      </DialogContent>
    </Dialog>
  )
}

/**
 * A trigger that opens the Irish language modal to a given page. Wrap any label.
 *
 * ```tsx
 * <IrishLanguageReference page={PAGES.VSO}>Word order</IrishLanguageReference>
 * ```
 */
export const IrishLanguageReference: React.FC<{
  page?: PageKey
  className?: string
  children: React.ReactNode
}> = ({ page = PAGES.HOME, className, children }) => {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          "cursor-help border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2"
        }
      >
        {children}
      </button>
      {/* Mount the modal only once opened: a closed trigger stays a plain button, so rendering it doesn't
          pull in the modal's hooks (useModalAnalytics) – keeps it cheap and test-friendly wherever a guide
          link is dropped. */}
      {open && (
        <IrishLanguageModal open onOpenChange={setOpen} defaultPage={page} />
      )}
    </>
  )
}

/**
 * A small "View reference guide" link that opens the Irish language modal to a page (defaults to
 * Pronunciation, since it is most often dropped next to a pronunciation section). The breadcrumb home
 * icon still reaches the full contents. A consistent, compact toggle to drop next to any section.
 */
export const ReferenceGuideLink: React.FC<{
  page?: PageKey
  label?: string
  className?: string
}> = ({ page = PAGES.PRONUNCIATION, label = "View guide", className }) => (
  <IrishLanguageReference
    page={page}
    className={
      className ??
      "inline-flex items-center gap-1 text-xs font-medium text-primary-600 no-underline hover:underline"
    }
  >
    <IconBook2 size={14} aria-hidden /> {label}
  </IrishLanguageReference>
)

export default IrishLanguageModal
